import {
  ArrowRightOnRectangleIcon,
  BellIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  LightBulbIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const sidebarSections = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", href: "/dashboard", icon: ChartBarIcon },
    ],
  },
  {
    label: "Management",
    items: [
      { name: "User Management", href: "/users", icon: UsersIcon },
      { name: "Care Centers", href: "/care-centers/register", icon: BuildingOffice2Icon },
      { name: "Contact Info", href: "/contact-info", icon: PhoneIcon },
    ],
  },
  {
    label: "Content",
    items: [
      { name: "Announcements", href: "/announcements", icon: BellIcon },
      { name: "Feedback", href: "/feedback", icon: ChatBubbleLeftRightIcon },
      { name: "FAQs", href: "/faqs", icon: QuestionMarkCircleIcon },
      { name: "Topics of Interest", href: "/topics", icon: LightBulbIcon },
    ],
  },
  {
    label: "Legal",
    items: [
      { name: "Privacy Policy", href: "/privacy-policy", icon: DocumentTextIcon },
      { name: "Terms of Use", href: "/terms-of-use", icon: DocumentTextIcon },
    ],
  },
  {
    label: "System",
    items: [
      { name: "App Version", href: "/app-version", icon: CloudArrowUpIcon },
      { name: "Profile", href: "/profile", icon: UserCircleIcon },
    ],
  },
];

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    onClose();
  };

  const avatarUrl = useMemo(() => {
    const username = user?.username || "Admin";
    return (
      user?.avatar ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=f47842&color=fff&size=128`
    );
  }, [user?.avatar, user?.username]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 sm:w-80 lg:w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } animate-slide-in-right`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Header */}
          <div className="flex items-center justify-between h-14 sm:h-16 px-4 bg-gradient-to-r from-primary-500 to-primary-600 shadow-soft">
            <div className="flex items-center min-w-0">
              <div className="h-7 w-7 sm:h-8 sm:w-8 bg-white rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
              </div>
              <h1 className="text-base sm:text-lg font-bold text-white truncate">Moro Admin</h1>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-1.5 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
              aria-label="Close sidebar"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 sm:px-4 py-4 sm:py-6 overflow-y-auto">
            {sidebarSections.map((section, sectionIndex) => (
              <div key={section.label} className={sectionIndex > 0 ? "mt-5" : ""}>
                <h3 className="px-3 mb-1.5 text-[11px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
                  {section.label}
                </h3>
                <div className="space-y-1 sm:space-y-1.5">
                  {section.items.map((item) => {
                    const isActive = location.pathname === item.href ||
                      (item.href !== "/profile" && location.pathname.startsWith(item.href) && item.href !== "/dashboard");
                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        onClick={onClose}
                        className={`flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive ? "sidebar-link-active" : "sidebar-link-inactive"
                        }`}
                      >
                        <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                        <span className="truncate">{item.name}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User info */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-3 sm:p-4">
            <NavLink
              to="/profile"
              onClick={onClose}
              className="flex items-center mb-3 sm:mb-4 p-2 sm:p-3 rounded-xl transition-colors duration-200 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              <img
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-full ring-2 ring-primary-500 flex-shrink-0"
                src={avatarUrl}
                alt={user?.username || "Admin"}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div className="ml-2 sm:ml-3 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.username || "Admin"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">
                  {user?.role || "admin"}
                </p>
              </div>
            </NavLink>

            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 sm:px-4 py-2 text-sm rounded-lg transition-colors duration-200 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowRightOnRectangleIcon className="mr-2 sm:mr-3 h-5 w-5 flex-shrink-0" />
              <span className="truncate">Sign out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
